import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Clock, Eye, Info, X, XCircle } from 'lucide-react';
import api, { extrairMensagemErro } from '../services/api';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';

const moeda = (valor?: number | string, codigo = 'KZ') =>
  `${codigo} ${Math.round(Number(valor || 0)).toLocaleString('pt-PT')}`;

const dataCurta = (valor?: string | null) =>
  valor ? new Date(valor).toLocaleDateString('pt-PT') : 'sem data';

const statusClasse = (status?: string) => {
  if (['ATIVA', 'ATIVO', 'APROVADO', 'CONFIRMADO', 'PAGA'].includes(String(status))) return 'status-done';
  if (['EXPIRADA', 'SUSPENSA', 'CANCELADA', 'REJEITADO'].includes(String(status))) return 'status-blocked';
  if (['PENDENTE', 'PENDENTE_ANALISE'].includes(String(status))) return 'status-warning';
  return '';
};

const ficheiroParaBase64 = (ficheiro: File) =>
  new Promise<string>((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result || ''));
    leitor.onerror = reject;
    leitor.readAsDataURL(ficheiro);
  });

const iconTimeline = (tipo?: string) => {
  if (tipo === 'CONFIRMADO') return <CheckCircle size={18} aria-hidden="true" />;
  if (tipo === 'REJEITADO') return <XCircle size={18} aria-hidden="true" />;
  if (tipo === 'EM_ANALISE') return <Clock size={18} aria-hidden="true" />;
  return <Info size={18} aria-hidden="true" />;
};

const classeTimeline = (tipo?: string) => {
  if (tipo === 'CONFIRMADO') return 'timeline-success';
  if (tipo === 'REJEITADO') return 'timeline-danger';
  if (tipo === 'EM_ANALISE') return 'timeline-warning';
  return 'timeline-info';
};

const planoDaReferencia = (referencia?: string | null) =>
  String(referencia || '').match(/PLANO:([A-Z_]+)/)?.[1] || '';

const SubscricaoEmpresa: React.FC = () => {
  const [estado, setEstado] = useState<any>(null);
  const [infoPagamento, setInfoPagamento] = useState<any>(null);
  const [planos, setPlanos] = useState<any[]>([]);
  const [planoSelecionado, setPlanoSelecionado] = useState('');
  const [ficheiro, setFicheiro] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [enviandoPagamento, setEnviandoPagamento] = useState(false);
  const [ficheiroInputKey, setFicheiroInputKey] = useState(0);
  const [comprovativosPagamento, setComprovativosPagamento] = useState<Record<string, File | null>>({});
  const [timeline, setTimeline] = useState<any>(null);
  const [carregandoTimeline, setCarregandoTimeline] = useState(false);
  const [paginacao, setPaginacao] = useState<MetadadosPaginacao>(paginacaoVazia);

  const carregar = useCallback(async (pagina = 1) => {
    setCarregando(true);
    setErro(null);
    try {
      const [estadoResp, infoResp, planosResp] = await Promise.all([
        api.get('/subscricao/estado', { params: { pagina, limite: paginacao.limite } }),
        api.get('/subscricao/info-pagamento'),
        api.get('/subscricao/planos')
      ]);
      setEstado(estadoResp.data.dados);
      setPaginacao(estadoResp.data.paginacao || paginacaoVazia);
      setInfoPagamento(infoResp.data.dados);
      setPlanos(planosResp.data.dados || []);
      const primeiroPago = (planosResp.data.dados || []).find((plano: any) => Number(plano.preco_mensal) > 0);
      setPlanoSelecionado((atual) => atual || primeiroPago?.codigo || '');
    } catch (erroReq: any) {
      setErro(extrairMensagemErro(erroReq, 'Erro ao carregar subscrição.'));
    } finally {
      setCarregando(false);
    }
  }, [paginacao.limite]);

  useEffect(() => {
    carregar(1);
  }, [carregar]);

  const concluirPagamento = async () => {
    if (!ficheiro || !planoSelecionado || enviandoPagamento) return;
    setErro(null);
    setMensagem(null);
    setEnviandoPagamento(true);
    try {
      const conteudo = await ficheiroParaBase64(ficheiro);
      const resposta = await api.post('/subscricao/concluir-pagamento', {
        plano: planoSelecionado,
        dias: 30,
        comprovativo: {
          nome_original: ficheiro.name,
          mime_type: ficheiro.type,
          conteudo_base64: conteudo
        }
      });
      setMensagem(resposta.data?.mensagem || 'Pagamento enviado para análise.');
      setFicheiro(null);
      setFicheiroInputKey((atual) => atual + 1);
      await carregar();
      setPlanoSelecionado('');
    } catch (erroReq: any) {
      setErro(extrairMensagemErro(erroReq, 'Erro ao concluir pagamento.'));
    } finally {
      setEnviandoPagamento(false);
    }
  };

  const abrirTimeline = async (pagamento: any) => {
    setErro(null);
    setMensagem(null);
    setTimeline({ pagamento, eventos: [] });
    setCarregandoTimeline(true);
    try {
      const resposta = await api.get(`/subscricao/pagamentos/${pagamento.id}/timeline`);
      setTimeline({ pagamento, ...(resposta.data?.dados || {}) });
    } catch (erroReq: any) {
      setTimeline(null);
      setErro(extrairMensagemErro(erroReq, 'Erro ao carregar acompanhamento do pagamento.'));
    } finally {
      setCarregandoTimeline(false);
    }
  };

  const enviarComprovativoPendente = async (pagamentoId: string) => {
    const comprovativo = comprovativosPagamento[pagamentoId];
    if (!comprovativo || enviandoPagamento) return;
    setErro(null);
    setMensagem(null);
    setEnviandoPagamento(true);
    try {
      const conteudo = await ficheiroParaBase64(comprovativo);
      const resposta = await api.post(`/subscricao/pagamentos/${pagamentoId}/comprovativo`, {
        comprovativo: {
          nome_original: comprovativo.name,
          mime_type: comprovativo.type,
          conteudo_base64: conteudo
        }
      });
      setMensagem(resposta.data?.mensagem || 'Comprovativo enviado para análise.');
      setComprovativosPagamento((atual) => ({ ...atual, [pagamentoId]: null }));
      await carregar();
    } catch (erroReq: any) {
      setErro(extrairMensagemErro(erroReq, 'Erro ao enviar comprovativo.'));
    } finally {
      setEnviandoPagamento(false);
    }
  };

  const iniciarNovoPagamento = (pagamento: any) => {
    const planoReferencia = planoDaReferencia(pagamento.referencia);
    if (planoReferencia) setPlanoSelecionado(planoReferencia);
    setFicheiro(null);
    setFicheiroInputKey((atual) => atual + 1);
    setErro(null);
    setMensagem('Selecione um comprovativo para submeter um novo pagamento.');
  };

  const subscricao = estado?.subscricao;
  const capacidade = estado?.capacidade;
  const pagamentos = estado?.pagamentos || [];

  return (
    <div className="workspace-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Subscrição</span>
          <h1>Plano e pagamento da empresa</h1>
          <p>Consulte a vigência do plano, os limites de utilizadores e envie comprovativos de pagamento manual.</p>
        </div>
        <button className="botao botao-secundario" type="button" onClick={() => carregar(paginacao.pagina)}>Atualizar</button>
      </header>

      {erro && <div className="alerta alerta-erro">{erro}</div>}
      {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
      {carregando && <div className="inline-loading"><div className="spinner"></div><span>A carregar subscrição...</span></div>}

      <section className="metric-grid admin-metric-grid">
        <div className="metric-card">
          <span>Plano atual</span>
          <strong>{subscricao?.plano?.nome || '-'}</strong>
        </div>
        <div className="metric-card">
          <span>Status</span>
          <strong><span className={`status-pill ${statusClasse(subscricao?.status)}`}>{subscricao?.status || '-'}</span></strong>
        </div>
        <div className="metric-card">
          <span>Vigência</span>
          <strong>{dataCurta(subscricao?.data_inicio)} - {dataCurta(subscricao?.data_fim)}</strong>
        </div>
        <div className="metric-card">
          <span>Utilizadores</span>
          <strong>{capacidade?.atual || 0} / {capacidade?.limite || 0}</strong>
        </div>
      </section>

      <section className="operation-grid">
        <div className="panel">
          <div className="panel-heading">
            <h2>Dados para pagamento</h2>
            <span className={`status-pill ${infoPagamento?.ativo ? 'status-done' : 'status-warning'}`}>{infoPagamento?.ativo ? 'ATIVO' : 'INDISPONÍVEL'}</span>
          </div>
          {infoPagamento ? (
            <div className="config-grid">
              <div><strong>Banco</strong><small>{infoPagamento.banco}</small></div>
              <div><strong>Titular</strong><small>{infoPagamento.titular_conta}</small></div>
              <div><strong>IBAN/Conta</strong><small>{infoPagamento.iban}</small></div>
              <div><strong>NIF</strong><small>{infoPagamento.nif || '-'}</small></div>
              <div><strong>Referência</strong><small>{infoPagamento.referencia_pagamento || '-'}</small></div>
              <div><strong>Instruções</strong><small>{infoPagamento.instrucoes || '-'}</small></div>
            </div>
          ) : (
            <div className="empty-state compact-state">Sem dados de pagamento configurados.</div>
          )}
        </div>

        <div className="panel">
          <div className="panel-heading">
            <h2>Renovar ou alterar plano</h2>
            <span className="status-pill status-warning">Manual</span>
          </div>
          <div className="admin-filters payment-form">
            <select value={planoSelecionado} onChange={(evento) => setPlanoSelecionado(evento.target.value)} disabled={enviandoPagamento}>
              <option value="">Escolha um plano</option>
              {planos.filter((plano) => Number(plano.preco_mensal) > 0).map((plano) => (
                <option key={plano.id} value={plano.codigo}>{plano.nome} - {moeda(plano.preco_mensal, plano.moeda)}</option>
              ))}
            </select>
            <input key={ficheiroInputKey} type="file" accept="application/pdf" onChange={(evento) => setFicheiro(evento.target.files?.[0] || null)} disabled={enviandoPagamento} />
            <button className="botao botao-primario" type="button" onClick={concluirPagamento} disabled={!planoSelecionado || !ficheiro || enviandoPagamento}>
              {enviandoPagamento ? 'A enviar...' : 'Concluir pagamento'}
            </button>
          </div>
        </div>
      </section>

      <section className="panel">
        <div className="panel-heading">
          <h2>Histórico de pagamentos</h2>
          <span className="status-pill status-done">{pagamentos.length}</span>
        </div>
        <div className="table-shell">
          <table>
            <thead><tr><th>Fatura</th><th>Valor</th><th>Status</th><th>Comprovativo</th><th>Motivo</th><th>Acompanhamento</th></tr></thead>
            <tbody>
              {pagamentos.map((pagamento: any) => (
                <tr key={pagamento.id}>
                  <td>{pagamento.fatura?.numero_fatura || pagamento.id}<small>{pagamento.referencia}</small></td>
                  <td>{moeda(pagamento.valor_pago, pagamento.moeda)}</td>
                  <td><span className={`status-pill ${statusClasse(pagamento.status)}`}>{pagamento.status}</span></td>
                  <td>{pagamento.comprovativo_nome || 'sem comprovativo'}</td>
                  <td>{pagamento.motivo_rejeicao || '-'}</td>
                  <td>
                    {String(pagamento.status) === 'PENDENTE' && (
                      <div className="table-upload">
                        <input type="file" accept="application/pdf" onChange={(evento) => setComprovativosPagamento((atual) => ({ ...atual, [pagamento.id]: evento.target.files?.[0] || null }))} disabled={enviandoPagamento} />
                        <button className="botao botao-primario botao-compacto" type="button" onClick={() => enviarComprovativoPendente(pagamento.id)} disabled={!comprovativosPagamento[pagamento.id] || enviandoPagamento}>
                          Enviar
                        </button>
                      </div>
                    )}
                    {String(pagamento.status) === 'REJEITADO' && (
                      <button className="botao botao-primario botao-compacto" type="button" onClick={() => iniciarNovoPagamento(pagamento)}>
                        Submeter novo pagamento
                      </button>
                    )}
                    <button className="botao botao-secundario botao-compacto" type="button" onClick={() => abrirTimeline(pagamento)}>
                      <Eye size={16} aria-hidden="true" />Ver progresso
                    </button>
                  </td>
                </tr>
              ))}
              {pagamentos.length === 0 && <tr><td colSpan={6}>Sem pagamentos para mostrar.</td></tr>}
            </tbody>
          </table>
          <Paginacao paginacao={paginacao} carregando={carregando} aoMudarPagina={carregar} />
        </div>
      </section>

      {timeline && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-conteudo payment-timeline-modal">
            <div className="panel-heading">
              <div>
                <h2>Acompanhamento do pagamento</h2>
                <small>{timeline.pagamento?.fatura?.numero_fatura || timeline.pagamento_id} · {moeda(timeline.pagamento?.valor_pago, timeline.pagamento?.moeda)}</small>
              </div>
              <button className="botao botao-secundario botao-compacto" type="button" onClick={() => setTimeline(null)} aria-label="Fechar acompanhamento">
                <X size={16} aria-hidden="true" />
              </button>
            </div>

            {carregandoTimeline ? (
              <div className="inline-loading"><div className="spinner"></div><span>A carregar acompanhamento...</span></div>
            ) : (
              <div className="payment-timeline">
                {(timeline.eventos || []).map((evento: any) => (
                  <div className={`payment-timeline-item ${classeTimeline(evento.tipo)}`} key={`${evento.tipo}-${evento.data_hora}`}>
                    <span className="payment-timeline-icon">{iconTimeline(evento.tipo)}</span>
                    <div className="payment-timeline-content">
                      <strong>{evento.titulo}</strong>
                      <small>{dataCurta(evento.data_hora)} {new Date(evento.data_hora).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</small>
                      <p>{evento.descricao}</p>
                      {evento.responsavel && <small>Responsável: {evento.responsavel}</small>}
                      {evento.motivo_rejeicao && <div className="timeline-rejection-reason"><strong>Motivo</strong><span>{evento.motivo_rejeicao}</span></div>}
                    </div>
                  </div>
                ))}
                {(timeline.eventos || []).length === 0 && <div className="empty-state compact-state">Sem eventos para este pagamento.</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscricaoEmpresa;
